
import React, { forwardRef, useState, useMemo, useCallback } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes } from 'utils/formUtils';
import { uniqBy, isEmpty } from 'lodash';
import GenericMappingForm from '../GenericMappingForm';
import message from "components/CustomMessage";
import { Form } from 'antd';
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { getEmptyCancellableRequest } from 'restAPI';

const CheckmarxOneMappingForm = forwardRef(function CheckmarxOneMappingForm({disableProjectFields = false, ...props}, ref) {
    const [form] = Form.useForm();
    const featureFlags = useFeatureFlag();

    const [mappedProjectList] = useState(() => {
        // this state contains the already mapped application/site
        // This is stored because the project and version dropdowns are paginated
        // and if the option selected is not on 2st page, then the ID is displayed as this is how select works
        if (props.record) {
          const { projectId, projectName } = props.record;
          return {
            projects: projectId ? [{ id: projectId, name: projectName }] : [],
          }
        }
        return { projects: [] }
      })
    
    const [options,setOptions] = useState(mappedProjectList.projects);
    
    const setOptionsFromPagination = (option) => {
        setOptions(prev => uniqBy([...prev,...option], 'id'));
    }

    const fetchProjects = useCallback((params) => {
      const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId,...params });
      promise.then((response) => {
        if(mappedProjectList['projects'].length > 0 && !isEmpty(response.projects)){
          response.projects = [...response.projects, ...mappedProjectList['projects']];
        }
        setOptionsFromPagination(response.projects)
      });
      return { promise, cancel };
    }, [props.toolName, mappedProjectList, props.loginConfigId]);


    const fetchBranches = useCallback((params) => {
      if (!params.project) {
        return getEmptyCancellableRequest([]);
      }
      const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
        login_id: props.loginConfigId,
        ...params
      });

      promise.then((response) => {
        if (response.projects) {
          response.projects = response.projects.map(branch => ({
            ...branch,
            id: branch.name 
          }));
        }
      }).catch((error) => {
        message.error("Problem fetching branches");
      });

      return { promise, cancel };
    }, [props.toolName, props.loginConfigId]);



    const parseExtraFieldsDataFromResponse = useCallback((config) => {
      const { projectId, branch } = config;
      return {
        projectId,
        branch,
        ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
      };
    }, []);

    const getExtraFieldsData = useCallback((config) => {
        const { projectId, branch } = config;
        const projectName = options.find(project => project.id === projectId)?.name;
        
        return {
        projectId,
        projectName,
        branch,
        branchName: branch, 
        //send the property received in config
        ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
        };
    }, [options]);

  const extraFields = useMemo(() => {
    const fields = [{
      name: 'projectId',
      colSpan: '12',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      label: 'Project Name',
      disabled: disableProjectFields,
      required: true,
      props: {
        autoFocus: !disableProjectFields,
        api: fetchProjects,
        onChange: (value, field) => {
          const { name } = field;
          const configurations = form.getFieldValue("configurations");
          if (configurations && configurations[name]) {
            configurations[name] = {
              ...configurations[name],
              branch: undefined,
            };
            form.setFieldsValue({ configurations: configurations });
          }
        }
      }
    }];

    if (featureFlags.ENABLE_CHECKMARXONE_SCAN_FILTERS) {
      fields.push({
        name: 'branch',
        colSpan: '12',
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        label: 'Branch Name',
        required: false,
        props: {
          placeholder: "Leave blank to select from any branch",
          detechAPIParamChange: true,
          setExtraProps: (fieldProps, currentField) => {
            const configurations = form.getFieldValue("configurations");
            const currentConfiguration = configurations?.[currentField.name] || {};
            const projectId = currentConfiguration?.projectId;
            const apiParams = { project: projectId };
            
            const extraProps = {
              api: fetchBranches,
              apiParams,
              resetOptionsProp: apiParams,
              disabled: !projectId,
              allowClear: true,
            };
            return extraProps;
          }
        },
      });
    }

    return fields;
  }, [fetchProjects, fetchBranches, props.loginConfigId, disableProjectFields, featureFlags.ENABLE_CHECKMARXONE_SCAN_FILTERS, form]);

  return (
    <GenericMappingForm
      form={form}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      {...props}
    />
  )
})

export default CheckmarxOneMappingForm;